import Uppy, {BasePlugin, type PluginOptions} from '@uppy/core';
import AwsS3MultipartUpload, {
	type AwsS3MultipartOptions,
} from '@uppy/aws-s3-multipart';
import TusUpload, {type CustomTusOptions} from '../tus';
import XHRUpload, {type XHRUploadOptions} from '@uppy/xhr-upload';
export type {XHRUploadOptions} from '@uppy/xhr-upload';
export type {CustomTusOptions as TusOptions} from '../tus';
export type {AwsS3MultipartOptions} from '@uppy/aws-s3-multipart';

export enum UploaderType {
	S3_MULTIPART = 'AwsS3Multipart',
	TUS = 'Tus',
	XHR = 'XHR',
}

interface UppyFileMultiDestinationMeta {
	uploader: UploaderType;
	[x: string | number]: any;
}

// see uppy private type `UploadHandler`
type UploaderPluginMethod = (fileIds: string[]) => Promise<void>;

export type Uploaders = {
	[UploaderType.S3_MULTIPART]?: [AwsS3MultipartUpload, UploaderPluginMethod];
	[UploaderType.TUS]?: [TusUpload, UploaderPluginMethod];
	[UploaderType.XHR]?: [XHRUpload, UploaderPluginMethod];
};

type Options = (
	| {
			useS3Multipart: true;
			// options are required when `useS3Multipart` is `true`
			s3MultipartOptions: AwsS3MultipartOptions;
	  }
	| {
			useS3Multipart?: false;
			s3MultipartOptions?: AwsS3MultipartOptions;
	  }
) &
	(
		| {
				useTus: true;
				// options are required when `useTus` is `true`
				tusOptions: CustomTusOptions;
		  }
		| {
				useTus?: false;
				tusOptions?: CustomTusOptions;
		  }
	) &
	(
		| {
				useXHR: true;
				// options are required when `useXHR` is `true`
				xhrOptions: XHRUploadOptions;
		  }
		| {
				useXHR?: false;
				xhrOptions?: XHRUploadOptions;
		  }
	);

export type UppyMultiDestinationOptions = Omit<
	PluginOptions,
	'replaceTargetContent'
> &
	Options;

/**
 * Multi destination upload plugin for `Uppy`\
 * Can define multiple uploader plugins and control the flow of files to specified destinations.\
 * When adding a file to the queue be sure to set the `meta.uploader` to a valid/defined `UploaderType` value
 */
export class UppyMultiDestination extends BasePlugin<UppyMultiDestinationOptions> {
	type = 'uploader';

	opts: UppyMultiDestinationOptions;

	/** defined uploader instances */
	#uploaders: Uploaders = {};

	/** HACK: prevent an uploader plugin from calling the actual `uppy.addUploader` method when this is `true` */
	#ignoreNextAddUploader = false;

	/**
	 * store the last installed uploader's upload fn
	 * @see #ignoreNextAddUploader
	 */
	#lastUploaderInstalled?: UploaderPluginMethod;

	/** number of upload plugins defined */
	get #uploadPluginCount() {
		return Object.keys(this.#uploaders).length;
	}

	/** multiple upload plugins defined */
	get #usingMultipleUploaders() {
		return this.#uploadPluginCount > 1;
	}

	constructor(uppy: Uppy, opts?: UppyMultiDestinationOptions) {
		super(uppy, opts);

		const defaultOptions: UppyMultiDestinationOptions = {};

		this.opts = {...defaultOptions, ...opts};
		this.id = this.opts.id || 'MultiDestination';

		this.handleUploadRequest = this.handleUploadRequest.bind(this);

		// if `ignoreNextAddUploader` is `true`, prevent the call from reaching uppy (when we call <plugin>.install())
		// we do not want the plugins we are managing to push themselves into uppy's `#uploaders` set
		const originalAddUploader = this.uppy.addUploader;
		this.uppy.addUploader = (fn) => {
			if (this.#ignoreNextAddUploader) {
				// unset and return
				this.#ignoreNextAddUploader = false;
				// we must store the reference as uppy has now made the s3 multipart plugin's upload method private
				// we can no longer call it directly from the uploader instance
				// rather than make a carve out, lets future proof it since we already have this entry point (hack)
				// this will then be stored in the `uploader` array for the given plugin
				this.#lastUploaderInstalled = fn;
				return;
			}
			// allow
			return originalAddUploader.call(this.uppy, fn);
		};
	}

	/**
	 * manage which upload plugin the files should be sent to
	 * @param fileIDs
	 * @returns Upload promise (for all neccessary uploaders)
	 */
	async handleUploadRequest(fileIDs: string[]) {
		const files = this.uppy.getState<UppyFileMultiDestinationMeta>().files;

		const uploaderFileIds: Record<UploaderType, string[]> = {
			[UploaderType.S3_MULTIPART]: [],
			[UploaderType.TUS]: [],
			[UploaderType.XHR]: [],
		};

		const uploaderTypes = Object.values(UploaderType);

		if (this.#usingMultipleUploaders) {
			// multiple destinations defined
			fileIDs.forEach((id) => {
				const file = files[id];

				if (!file) throw new Error(`file (${id}) not found`);

				if (!uploaderTypes.includes(file.meta.uploader))
					throw new Error(
						`'${file.meta.uploader}' is not a valid \`uploader\` type`,
					);

				uploaderFileIds[file.meta.uploader as UploaderType].push(id);
			});
		} else {
			// just one destination defined
			uploaderFileIds[Object.keys(this.#uploaders)[0] as UploaderType] =
				fileIDs;
		}

		return Promise.all(
			uploaderTypes.map((type) =>
				this.#uploadToDestination(type, uploaderFileIds[type]),
			),
		).then(() => {
			// expects void, not void[]
			return;
		});
	}

	/**
	 * Initialize upload plugin and set any (abstract) neccessary flags/hacks
	 * @param fn Plugin init callback
	 */
	#initializeUploadPlugin<F extends () => any>(fn: F): ReturnType<F> {
		this.#ignoreNextAddUploader = true;
		return fn();
	}

	/**
	 * Get an upload plugin registered by this plugin
	 * @param uploaderType
	 * @returns Upload plugin
	 */
	getPlugin<T extends UploaderType>(uploaderType: T) {
		return this.#uploaders[uploaderType];
	}

	/**
	 * Upload files to destination (upload plugin)
	 * @param uploaderType
	 * @param fileIDs
	 * @returns Upload promise
	 */
	#uploadToDestination<T extends UploaderType>(
		uploaderType: T,
		fileIDs: string[],
	) {
		switch (uploaderType) {
			case UploaderType.S3_MULTIPART:
				return this.#uploadToS3Multipart(fileIDs);
			case UploaderType.TUS:
				return this.#uploadToTus(fileIDs);
			case UploaderType.XHR:
				return this.#uploadToXHR(fileIDs);
		}
	}

	/** handle uploading via s3 multipart */
	#uploadToS3Multipart(fileIDs: string[]) {
		if (!fileIDs.length) return;
		const plugin = this.getPlugin(UploaderType.S3_MULTIPART);
		if (!plugin) {
			console.warn(
				`files passed for s3 multipart but s3 multipart is not configured`,
			);
			return;
		}
		return plugin[1](fileIDs);
	}

	/** handle uploading via tus */
	#uploadToTus(fileIDs: string[]) {
		if (!fileIDs.length) return;
		const plugin = this.getPlugin(UploaderType.TUS);
		if (!plugin) {
			console.warn(`files passed for tus but tus is not configured`);
			return;
		}
		return plugin[1](fileIDs);
	}

	/** handle uploading via xhr */
	#uploadToXHR(fileIDs: string[]) {
		if (!fileIDs.length) return;
		const plugin = this.getPlugin(UploaderType.XHR);
		if (!plugin) {
			console.warn(`files passed for xhr but xhr is not configured`);
			return;
		}
		return plugin[1](fileIDs);
	}

	/** install s3 multipart upload plugin */
	#installS3MultipartPlugin() {
		if (!this.opts.useS3Multipart) return;
		return this.#initializeUploadPlugin(() => {
			const uploadPlugin = new AwsS3MultipartUpload(
				this.uppy,
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				this.opts.s3MultipartOptions!,
			);
			uploadPlugin.install();

			const uploader = (this.#uploaders[UploaderType.S3_MULTIPART] = [
				uploadPlugin,
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				this.#lastUploaderInstalled!,
			]);
			return uploader;
		});
	}

	/** install tus upload plugin */
	#installTusPlugin() {
		if (!this.opts.useTus) return;

		return this.#initializeUploadPlugin(() => {
			const uploadPlugin = new TusUpload(
				this.uppy,
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				this.opts.tusOptions!,
			);
			uploadPlugin.install();

			const uploader = (this.#uploaders[UploaderType.TUS] = [
				uploadPlugin,
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				this.#lastUploaderInstalled!,
			]);

			const originalHandleFileAdded = uploadPlugin.handleFileAdded;
			// tus is the only plugin that we use (so far) that is trying to add to the queue at file add instead of upload start
			uploadPlugin.handleFileAdded = (file) => {
				if (
					this.#usingMultipleUploaders &&
					file.meta.uploader !== UploaderType.TUS
				)
					return;
				originalHandleFileAdded.call(uploader, file);
			};
			return uploader;
		});
	}

	/** install xhr upload plugin */
	#installXHRPlugin() {
		if (!this.opts.useXHR) return;

		return this.#initializeUploadPlugin(() => {
			const uploadPlugin = new XHRUpload(this.uppy, this.opts.xhrOptions);
			uploadPlugin.install();

			const uploader = (this.#uploaders[UploaderType.XHR] = [
				uploadPlugin,
				// was stored during install
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				this.#lastUploaderInstalled!,
			]);

			const {capabilities} = this.uppy.getState<UppyFileMultiDestinationMeta>();
			// all or nothing on resumable
			// and xhr is not resumable
			this.uppy.setState({
				capabilities: {
					...capabilities,
					resumableUploads: false,
				},
			});

			// warn if we have turned `resumableUploads` off when it would have otherwise been turned on
			if (this.opts.useS3Multipart || this.opts.useTus) {
				console.warn(
					`\`resumableUploads\` have been disabled due to \`useXHR\` being \`true\``,
				);
			}

			return uploader;
		});
	}

	/** install this plugin */
	install() {
		this.uppy.addUploader(this.handleUploadRequest);

		this.#installS3MultipartPlugin();
		this.#installTusPlugin();
		this.#installXHRPlugin();

		if (!this.#uploadPluginCount) throw new Error('No uploaders defined');
	}

	/** uninstall this plugin */
	uninstall() {
		// uninstall all upload plugins
		Object.values(this.#uploaders).forEach((uploader) =>
			uploader[0].uninstall(),
		);
	}
}

export default UppyMultiDestination;
